import { Action as AccessTokenAction, ActionType as AccessTokenActionType } from '~/AccessToken';
import { CaLabeling, ProductUpdate } from '~/Api/User/ProductGroup/CaLabeling';

import { Action, ActionTypes } from './Action';

export interface CaLabelingState {
	caLabeling: CaLabeling | null;
	isFetching: boolean;
	request: Promise<any>;
}

type UpdateState =
	| {
			basketVersion: string;
			name: 'scheduled';
			products: ProductUpdate[];
			timeoutId: NodeJS.Timeout;
	  }
	| { name: 'fetching'; request: Promise<any> | null }
	| { name: 'updated' }
	| { name: 'failed' };

export interface Update {
	groupId: string;
	state: UpdateState;
}

export interface State {
	byId: { [key in string]: CaLabelingState };
	update: Update | null;
}

export const createState = (): State => ({
	byId: {},
	update: null,
});

// tslint:disable-next-line: cyclomatic-complexity
export const reducer = (state: State = createState(), action: Action | AccessTokenAction): State => {
	switch (action.type) {
		case ActionTypes.request:
			return {
				...state,
				byId: {
					...state.byId,
					[action.groupId]: {
						caLabeling: state.byId[action.groupId]?.caLabeling ?? null,
						isFetching: true,
						request: action.request,
					},
				},
			};

		case ActionTypes.receive: {
			const caLabeling = state.byId[action.groupId];
			if (caLabeling?.request !== action.request) {
				return state;
			}

			if (action.caLabeling === null) {
				const byId = { ...state.byId };
				delete byId[action.groupId];
				return { ...state, byId };
			}

			return {
				...state,
				byId: {
					[action.groupId]: {
						...caLabeling,
						caLabeling: action.caLabeling,
						isFetching: false,
					},
				},
			};
		}

		case ActionTypes.prune: {
			const byId = { ...state.byId };
			delete byId[action.groupId];
			return { ...state, byId };
		}

		case ActionTypes.scheduleUpdate: {
			const updateState = state.update?.state;
			if (updateState?.name === 'scheduled') {
				clearTimeout(updateState.timeoutId);
			}

			return {
				...state,
				update: {
					groupId: action.groupId,
					state: {
						basketVersion: action.basketVersion,
						name: 'scheduled',
						products: action.products,
						timeoutId: action.timeoutId,
					},
				},
			};
		}

		case ActionTypes.requestUpdate: {
			return {
				...state,
				update: {
					groupId: action.groupId,
					state: { name: 'fetching', request: action.request },
				},
			};
		}

		case ActionTypes.receiveUpdate: {
			const updateState = state.update?.state;
			if (updateState?.name === 'fetching' && updateState.request !== action.request) {
				return state;
			}

			const byId = { ...state.byId };
			if (action.caLabeling !== null && action.groupId in byId) {
				byId[action.groupId] = {
					...byId[action.groupId],
					caLabeling: action.caLabeling,
				};
			}

			return {
				...state,
				byId,
				update: {
					groupId: action.groupId,
					state: action.caLabeling !== null ? { name: 'updated' } : { name: 'failed' },
				},
			};
		}

		case AccessTokenActionType.receive:
			return action.userChanged ? createState() : state;

		case AccessTokenActionType.receiveLogin:
			return createState();

		default:
			return state;
	}
};

export const selectCaLabeling = (state: State, groupId: string): CaLabelingState | null => state.byId[groupId];
